/** What the POI-1 engine is doing, and what it decided (docs/poi1.md).
 *
 * `status` is the engine's in-memory runtime (a restart empties it until the
 * next tick replays the day); `decisions` is the durable trail.
 *
 * Levels, stop and target are INDEX prices. `cap_price` is the only OPTION
 * price: the premium at which the per-lot loss cap closes the trade.
 */

import { getJson } from "./http";
import type { EmaTrendDecision } from "./emaTrendApi";

export interface Poi1Levels {
  side: string;
  /** lower_wick / upper_wick / green_body / red_body — which part of the
   * opening candle was biggest. */
  shape: string;
  level: number;
  stop: number;
  /** Null once a target was found already passed; the trade runs without one. */
  target: number | null;
  /** True once the 09:30 update to the 15-min candle's edges has applied. */
  widened: boolean;
}

export interface Poi1Position {
  contract_symbol: string;
  lots: number;
  lot_size: number;
  cap_price: number | null;
  /** False while measured from the quote at entry, true once from the fill. */
  cap_from_fill: boolean;
  cap_rupees_per_lot: number;
}

/** waiting (before 09:20) · watching · armed · in_trade · done */
export type Poi1Phase = "waiting" | "watching" | "armed" | "in_trade" | "done";

export interface Poi1Status {
  /** False: no tick since the last restart — NOT flat. */
  tracking: boolean;
  trade_date: string | null;
  last_tick_at: string | null;
  underlying: string | null;
  last_spot: number | null;
  phase: Poi1Phase | null;
  levels: Poi1Levels | null;
  /** IST "HH:MM:SS" — the first minute the level may fill in. */
  armed_from: string | null;
  entry_at: string | null;
  /** Why the day ended without a trade. */
  over: string | null;
  position: Poi1Position | null;
}

export type Poi1Decision = EmaTrendDecision;

export const poi1Api = {
  status(strategyId: string): Promise<Poi1Status> {
    return getJson<Poi1Status>(`/api/poi1/status?strategy_id=${encodeURIComponent(strategyId)}`);
  },
  decisions(strategyId: string): Promise<Poi1Decision[]> {
    return getJson<Poi1Decision[]>(
      `/api/poi1/decisions?strategy_id=${encodeURIComponent(strategyId)}`,
    );
  },
};
